import type { Dispatch, SetStateAction } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import type {
  LessonAiDraft,
  LessonAiTestDraft,
  TeacherLessonTestVisibility,
} from "../../auth/api-client";
import { AutoResizeTextarea } from "../../components/AutoResizeTextarea";
import { LessonCodeEditor } from "./LessonCodeEditor";

const DEFAULT_NEW_TEST_CODE = `def test_main_py_is_nonempty():
    import pathlib
    text = pathlib.Path("main.py").read_text(encoding="utf-8")
    assert len(text.strip()) > 0, "Write something in main.py"
`;

export interface LessonChecksSectionProps {
  readonly draft: LessonAiDraft;
  readonly setDraft: Dispatch<SetStateAction<LessonAiDraft>>;
  readonly updateTest: (index: number, patch: Partial<LessonAiTestDraft>) => void;
}

export function LessonChecksSection({ draft, setDraft, updateTest }: LessonChecksSectionProps) {
  return (
    <div className="lesson-ws-section">
      <p className="muted small lesson-ws-section-lede">
        Python checks named <code className="fineprint">test_*</code> run in the browser against the student
        workspace (typically <code className="fineprint">main.py</code>). Write{" "}
        <code className="fineprint">def test_name():</code> with no parameters, and capture printed text inside
        the check. Result detail controls how
        much students see: <em>Detailed</em> shows full output, <em>Summary</em> is shorter,{" "}
        <em>Hidden</em> reveals only pass or fail.
      </p>

      {draft.tests.length === 0 ? (
        <p className="muted small">No automated checks yet. Add one below or generate them with AI.</p>
      ) : null}

      {draft.tests.map((t, i) => (
        <fieldset key={i} className="teacher-step-fieldset">
          <legend className="fineprint">Check {i + 1}</legend>
          <label className="field">
            <span className="field-label">Name</span>
            <input
              className="input"
              value={t.name}
              onChange={(ev) => updateTest(i, { name: ev.target.value })}
              placeholder="e.g. At least four print() calls"
            />
          </label>
          <label className="field">
            <span className="field-label">Teacher note (optional)</span>
            <AutoResizeTextarea
              className="input"
              minRows={2}
              value={t.description ?? ""}
              onChange={(ev) => updateTest(i, { description: ev.target.value || undefined })}
              placeholder="Shown to students depending on the result-detail setting below."
            />
          </label>
          <label className="field">
            <span className="field-label">Test code</span>
            <LessonCodeEditor
              value={t.testCode}
              onChange={(v) => updateTest(i, { testCode: v })}
              ariaLabel={`Test code for check ${i + 1}`}
              minHeight="9rem"
            />
          </label>
          <div className="teacher-toolbar-form" style={{ alignItems: "flex-end" }}>
            <label className="field" style={{ flex: "1 1 12rem", minWidth: "10rem", marginBottom: 0 }}>
              <span className="field-label">Result detail for students</span>
              <select
                className="input"
                value={t.visibility ?? "detailed"}
                onChange={(ev) =>
                  updateTest(i, { visibility: ev.target.value as TeacherLessonTestVisibility })
                }
              >
                <option value="detailed">Detailed</option>
                <option value="summary">Summary</option>
                <option value="hidden">Hidden</option>
              </select>
            </label>
          </div>
          <label
            className="muted small"
            style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
          >
            <input
              type="checkbox"
              checked={t.isRequired !== false}
              onChange={(ev) => updateTest(i, { isRequired: ev.target.checked })}
            />
            Required for a passing submission
          </label>
          <button
            type="button"
            className="btn secondary danger tight"
            onClick={() =>
              setDraft((d) => ({
                ...d,
                tests: d.tests.filter((_, j) => j !== i),
              }))
            }
          >
            <FontAwesomeIcon icon={faTrash} /> Remove check
          </button>
        </fieldset>
      ))}

      <button
        type="button"
        className="btn secondary"
        onClick={() =>
          setDraft((d) => ({
            ...d,
            tests: [
              ...d.tests,
              {
                name: "New check",
                testCode: DEFAULT_NEW_TEST_CODE,
                isRequired: true,
                visibility: "detailed" as const,
              },
            ],
          }))
        }
      >
        <FontAwesomeIcon icon={faPlus} /> Add check
      </button>
    </div>
  );
}
